import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { Info } from "components/elements";
import {
  TiVoDeviceSource,
  useTiVoDeviceData,
} from "components/form/fields/TiVoTargets";
import {
  ReleaseStage,
  TiVoPublications,
  TiVoTargets,
  useVariantPublications,
} from "hooks/tivoApps";
import { unique } from "utils/array";

import { getNextReleaseStages } from "../../utils";
import { TEXTS } from "../constants";
import styles from "./PublicationsWarning.scss";

const ITEM_LIMIT = 5;

const RELEASE_STAGES_DEFAULT: ReleaseStage[] = [];

export type PublicationsWarningProps = {
  releaseStages?: ReleaseStage[];
  targets: TiVoTargets;
};

function filterPublicationByReleaseStages(
  publication: TiVoPublications,
  releaseStages: ReleaseStage[]
) {
  const result: TiVoPublications = {
    ...publication,
    platforms: [],
  };
  for (const platformPub of publication.platforms) {
    const platformId = platformPub.platformId;
    for (const [oemId, oemPub] of Object.entries(platformPub.publications)) {
      for (const rs of releaseStages) {
        const rsPub = oemPub[rs];
        if (!rsPub) {
          continue;
        }
        let resultPlatformPub = result.platforms.find(
          ({ platformId: id }) => id === platformId
        );
        if (!resultPlatformPub) {
          resultPlatformPub = { ...platformPub, publications: {} };
          result.platforms.push(resultPlatformPub);
        }
        let resultOemPub = resultPlatformPub.publications[oemId];
        if (!resultOemPub) {
          resultOemPub = {};
          resultPlatformPub.publications[oemId] = resultOemPub;
        }
        resultOemPub[rs] = rsPub;
      }
    }
  }
  return result;
}

export function getPublicationsOutsideTargets(
  devices: TiVoDeviceSource,
  publication: TiVoPublications,
  releaseStages: ReleaseStage[],
  targets: TiVoTargets
) {
  const result: Record<keyof TiVoTargets, { id: string; name: string }[]> = {
    clientTypes: [],
    oems: [],
    platforms: [],
  };
  // All platforms are targeted.
  if (!targets.platforms || targets.platforms.length === 0) {
    return result;
  }
  const publicationFiltered = filterPublicationByReleaseStages(
    publication,
    releaseStages
  );
  for (const platformPub of publicationFiltered.platforms) {
    const platformId = platformPub.platformId;
    // The platform is published but not targered.
    if (!targets.platforms.includes(platformId)) {
      result.platforms.push({
        id: platformId,
        name: devices.getDeviceLabel(platformId) || platformId,
      });
      continue;
    }
    // All OEMs or all OEMs in the platform are targeted.
    if (
      !targets.oems ||
      targets.oems.length === 0 ||
      !targets.oems.some((oemId) =>
        devices.getOemIds([platformId]).includes(oemId)
      )
    ) {
      continue;
    }
    for (const [oemId, oemPub] of Object.entries(platformPub.publications)) {
      // The OEM is published but not targeted.
      if (!targets.oems.includes(oemId)) {
        result.oems.push({
          id: oemId,
          name: devices.getDeviceLabel(oemId) || oemId,
        });
        continue;
      }
      // All client types or all client types in the OEM are targeted.
      if (
        !targets.clientTypes ||
        targets.clientTypes.length === 0 ||
        !targets.clientTypes.some((clientId) =>
          devices.getClientTypeIds([platformId], [oemId]).includes(clientId)
        )
      ) {
        continue;
      }
      let clientIds: string[] = [];
      for (const rs of releaseStages) {
        const rsPub = oemPub[rs];
        if (!rsPub) {
          continue;
        }
        // All OEM's client types are published.
        if (rsPub.length === 0) {
          clientIds = devices.getClientTypeIds([platformId], [oemId]);
          break;
        }
        clientIds = unique(clientIds, rsPub);
      }
      for (const clientId of clientIds) {
        // The client type is published but not targeted.
        if (!targets.clientTypes.includes(clientId)) {
          result.clientTypes.push({
            id: clientId,
            name: devices.getDeviceLabel(clientId) || clientId,
          });
        }
      }
    }
  }
  return result;
}

export function PublicationsWarning({
  releaseStages = RELEASE_STAGES_DEFAULT,
  targets,
}: PublicationsWarningProps) {
  const { id, variantId } = useParams();
  const devices = useTiVoDeviceData();
  const { data, isSuccess } = useVariantPublications(id, variantId);

  const message = useMemo(() => {
    const nextReleaseStages = getNextReleaseStages(releaseStages);
    if (!isSuccess || nextReleaseStages.length === 0) {
      return null;
    }
    const conflicts = getPublicationsOutsideTargets(
      devices,
      data.results,
      nextReleaseStages,
      targets
    );
    for (const level of ["platforms", "oems", "clientTypes"] as const) {
      const items = conflicts[level].map(({ name }) => name);
      if (items.length === 0) {
        continue;
      }
      let levelStr: string = level;
      switch (level) {
        case "clientTypes":
          levelStr = "client types";
          break;
        case "oems":
          levelStr = "OEMs";
          break;
      }
      const itemsStr =
        items.length > ITEM_LIMIT
          ? `${items.slice(0, ITEM_LIMIT - 1).join(", ")} and ${items.length - ITEM_LIMIT + 1} others`
          : items.join(", ");
      return TEXTS.SHARED.PUBLICATION_WARNING_MESSAGE.replace(
        "#{level}",
        levelStr
      ).replace("#{items}", itemsStr);
    }
    return null;
  }, [data, devices, isSuccess, releaseStages, targets]);

  if (!message) {
    return null;
  }

  return (
    <Info className={styles.info} type="warning">
      {message}
    </Info>
  );
}
